<script setup lang="ts" generic="T extends string">
/**
 * Choix unique dans une liste (boutons radio sur mesure). Chaque option est une ligne de 52 px
 * minimum, séparée par un filet ; l'option choisie porte une pastille bleue et un fond doux.
 */
import { RadioGroupIndicator, RadioGroupItem, RadioGroupRoot } from 'reka-ui'

import type { IconName } from '../icons/registry'
import Icon from './Icon.vue'

export interface Choice<V extends string> {
  value: V
  label: string
  description?: string
  icon?: IconName
  disabled?: boolean
}

defineProps<{ label: string; options: readonly Choice<T>[] }>()

const model = defineModel<T>()
</script>

<template>
  <RadioGroupRoot v-model="model" :aria-label="label" class="flex flex-col">
    <RadioGroupItem
      v-for="option in options"
      :key="option.value"
      :value="option.value"
      :disabled="option.disabled"
      class="flex min-h-control w-full items-center gap-3 border-b border-line px-1 py-3 text-left transition-colors duration-80 last:border-b-0 active:bg-surface data-[disabled]:text-ink-disabled"
    >
      <Icon v-if="option.icon" :name="option.icon" :size="24" class="text-ink-muted" />
      <span class="flex flex-1 flex-col gap-0.5">
        <span class="text-body text-ink">{{ option.label }}</span>
        <span v-if="option.description" class="text-caption text-ink-muted">{{
          option.description
        }}</span>
      </span>
      <span
        class="flex size-5.5 shrink-0 items-center justify-center rounded-full border-2 border-ink-muted"
        :class="model === option.value ? 'border-accent-strong' : ''"
        aria-hidden="true"
      >
        <RadioGroupIndicator class="size-2.5 rounded-full bg-accent-strong" />
      </span>
    </RadioGroupItem>
  </RadioGroupRoot>
</template>
